import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from './AuthContext';
import { apiRequest } from '../services/apiClient';
import {
  buildDefaultTenantConfig,
  getTerminologyValue,
  mergeTenantTerminology
} from '../config/tenantTerminology';
import {
  buildEnabledModuleSet,
  buildResolvedModuleCatalog,
  filterNavigationByEnabledModules,
  getModuleMetadata
} from '../config/tenantModules';
import {
  formatRoleLabel as baseFormatRoleLabel,
  formatWorkspaceLabel as baseFormatWorkspaceLabel,
  getNavigationForRole as baseGetNavigationForRole,
  resolveRouteModuleId
} from '../config/appRoutes';

const TenantConfigContext = createContext(null);

function buildFallbackConfiguration(user) {
  return buildDefaultTenantConfig({
    companyId: user?.companyId ?? null,
    companyDisplayName: user?.companyName || '',
    terminology: mergeTenantTerminology({})
  });
}

export function TenantConfigProvider({ children }) {
  const auth = useContext(AuthContext);
  const user = auth?.user || null;
  const isAuthenticated = Boolean(auth?.isAuthenticated);
  const isAuthLoading = Boolean(auth?.isLoading);
  const companyId = user?.companyId ?? null;
  const companyName = user?.companyName || '';
  const viewingClientPortal = Boolean(user?.viewingClientPortal);
  const fallbackConfiguration = useMemo(
    () => buildFallbackConfiguration({ companyId, companyName }),
    [companyId, companyName]
  );
  const [configuration, setConfiguration] = useState(fallbackConfiguration);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      const fallback = buildFallbackConfiguration(null);
      setConfiguration(fallback);
      setLoading(false);
      setError('');
      return fallback;
    }

    setLoading(true);
    setError('');
    try {
      const payload = await apiRequest('/api/tenant-configuration');
      const payloadConfiguration = payload?.configuration || {};
      const resolved = buildDefaultTenantConfig({
        ...fallbackConfiguration,
        ...payloadConfiguration,
        modulesConfigured: payloadConfiguration.modulesConfigured === true,
        enabledModules: Array.isArray(payloadConfiguration.enabledModules)
          ? payloadConfiguration.enabledModules
          : fallbackConfiguration.enabledModules
      });
      setConfiguration(resolved);
      return resolved;
    } catch (err) {
      const fallback = buildFallbackConfiguration({ companyId, companyName });
      setConfiguration(fallback);
      setError(err?.message || 'Unable to load tenant configuration.');
      return fallback;
    } finally {
      setLoading(false);
    }
  }, [companyId, companyName, fallbackConfiguration, isAuthenticated]);

  useEffect(() => {
    if (isAuthLoading) return;
    if (!isAuthenticated) {
      setConfiguration(buildFallbackConfiguration(null));
      setLoading(false);
      setError('');
      return;
    }
    refresh();
  }, [companyId, companyName, isAuthLoading, isAuthenticated, refresh, viewingClientPortal]);

  const enabledModules = useMemo(
    () => configuration?.enabledModules || fallbackConfiguration.enabledModules,
    [configuration?.enabledModules, fallbackConfiguration.enabledModules]
  );
  const enabledModuleSet = useMemo(
    () => buildEnabledModuleSet(enabledModules),
    [enabledModules]
  );
  const resolvedModules = useMemo(
    () => buildResolvedModuleCatalog(enabledModuleSet),
    [enabledModuleSet]
  );
  const moduleMetadata = useMemo(
    () => resolvedModules.reduce((accumulator, module) => {
      accumulator[module.id] = module;
      return accumulator;
    }, {}),
    [resolvedModules]
  );

  const isModuleEnabled = useCallback((moduleId) => {
    if (!moduleId) return true;
    return enabledModuleSet.has(moduleId);
  }, [enabledModuleSet]);

  const readModuleMetadata = useCallback((moduleId) => (
    moduleMetadata[moduleId] || getModuleMetadata(moduleId)
  ), [moduleMetadata]);

  const value = useMemo(() => ({
    configuration,
    enabledModules,
    resolvedModules,
    moduleMetadata,
    dependencyHelpers: {
      getBlockingRouteModuleId: resolveRouteModuleId
    },
    isModuleEnabled,
    getModuleMetadata: readModuleMetadata,
    loading,
    error,
    refresh
  }), [configuration, enabledModules, resolvedModules, moduleMetadata, isModuleEnabled, readModuleMetadata, loading, error, refresh]);

  return (
    <TenantConfigContext.Provider value={value}>
      {children}
    </TenantConfigContext.Provider>
  );
}

export function useTenantConfig() {
  const context = useContext(TenantConfigContext);
  if (context) return context;
  const fallbackConfiguration = buildFallbackConfiguration(null);
  const fallbackEnabledSet = buildEnabledModuleSet(fallbackConfiguration.enabledModules);
  return {
    configuration: fallbackConfiguration,
    enabledModules: fallbackConfiguration.enabledModules,
    resolvedModules: buildResolvedModuleCatalog(fallbackEnabledSet),
    moduleMetadata: {},
    dependencyHelpers: {
      getBlockingRouteModuleId: resolveRouteModuleId
    },
    isModuleEnabled: (moduleId) => !moduleId || fallbackEnabledSet.has(moduleId),
    getModuleMetadata,
    loading: false,
    error: '',
    refresh: async () => buildFallbackConfiguration(null)
  };
}

export function useTerminology() {
  const {
    configuration,
    enabledModules,
    loading,
    error,
    refresh
  } = useTenantConfig();
  const terminology = useMemo(
    () => mergeTenantTerminology(configuration?.terminology || {}),
    [configuration?.terminology]
  );
  const enabledModuleSet = useMemo(
    () => buildEnabledModuleSet(enabledModules),
    [enabledModules]
  );

  return useMemo(() => ({
    terminology,
    companyDisplayName: configuration?.companyDisplayName || '',
    industryKey: configuration?.industryKey || 'general_operations',
    logoUrl: configuration?.logoUrl || null,
    enabledModules,
    loading,
    error,
    refresh,
    t: (path, fallback = '') => getTerminologyValue(terminology, path, fallback),
    formatRoleLabel: (roleKey) => baseFormatRoleLabel(roleKey, terminology),
    formatWorkspaceLabel: (workspace) => baseFormatWorkspaceLabel(workspace, terminology),
    getNavigationForRole: (role) => filterNavigationByEnabledModules(
      baseGetNavigationForRole(role, terminology),
      enabledModuleSet,
      resolveRouteModuleId
    )
  }), [configuration?.companyDisplayName, configuration?.industryKey, configuration?.logoUrl, terminology, enabledModules, enabledModuleSet, loading, error, refresh]);
}

export function useTenantModules() {
  const {
    configuration,
    enabledModules,
    resolvedModules,
    moduleMetadata,
    isModuleEnabled,
    getModuleMetadata: getMetadata
  } = useTenantConfig();

  return useMemo(() => ({
    enabledModules,
    resolvedModules,
    moduleMetadata,
    isModuleEnabled,
    getModuleMetadata: getMetadata,
    configuration
  }), [configuration, enabledModules, resolvedModules, moduleMetadata, isModuleEnabled, getMetadata]);
}

export { TenantConfigContext };
